import { useMemo } from 'react';
// Types
import { GetStoreBySlug_store } from 'api/store/types/GetStoreBySlug';
import { GetAllHomePageData_featuredPpvEvents } from 'api/homePage/types/GetAllHomePageData';
// Hooks
import { useGetFeaturedStreams, useGetAthleteStreams } from 'hooks';
// Components
import FeaturedStreamsCarousel from 'components/HomePage/components/V3/FeaturedStreamsCarousel/FeaturedStreamsCarousel';

type ProfileFeaturedStreamProps = {
  store?: GetStoreBySlug_store;
  isProfileOwnerInFeaturedStreams: boolean;
};

const ProfileFeaturedStreams = ({
  store,
  isProfileOwnerInFeaturedStreams,
}: ProfileFeaturedStreamProps) => {
  const { data: featuredStreamsData } = useGetFeaturedStreams();

  // Fetch athlete streams when profile owner is NOT in featured streams
  // In that case, we show the athlete's latest stream in featured format
  const shouldFetchAthleteStreams = !isProfileOwnerInFeaturedStreams;

  const { data: athleteStreamsData } = useGetAthleteStreams({
    storeId: shouldFetchAthleteStreams && store?.id ? store.id : undefined,
    liveAndUpcomingStreamsInput: { limit: 1, offset: 0 },
    pastStreamsInput: { limit: 1, offset: 0 },
  });

  // Filter featured streams to only show ones where the owner is a participant or host
  const filteredFeaturedStreams = useMemo((): GetAllHomePageData_featuredPpvEvents[] => {
    if (!store?.id || !featuredStreamsData?.featuredPpvEvents) {
      return [];
    }

    const allFeaturedStreams = featuredStreamsData.featuredPpvEvents || [];

    return allFeaturedStreams.filter((featuredStream) => {
      const stream = featuredStream.stream;
      const isParticipant =
        stream.participants?.some(
          (participant) => participant.id === store.id
        ) || false;
      const isOwner = stream.store.id === store.id;

      return isParticipant || isOwner;
    });
  }, [store?.id, featuredStreamsData?.featuredPpvEvents]);

  // Convert athlete streams to featured format
  // Only show the stream with the latest date
  const convertedStreams = useMemo((): GetAllHomePageData_featuredPpvEvents[] => {
    if (!athleteStreamsData) return [];

    const allStreams = [
      ...(athleteStreamsData.liveAndUpcomingStreams?.entities || []),
      ...(athleteStreamsData.pastStreams?.entities || []),
    ];

    // Sort by date (latest first) and take only the most recent one
    const sortedStreams = allStreams.sort((a, b) => {
      const dateA = a.scheduleDateUtc
        ? new Date(a.scheduleDateUtc).getTime()
        : 0;
      const dateB = b.scheduleDateUtc
        ? new Date(b.scheduleDateUtc).getTime()
        : 0;
      return dateB - dateA; // Descending order (latest first)
    });

    // Take only the latest stream
    const latestStream = sortedStreams[0];
    if (!latestStream) return [];

    return [
      {
        __typename: 'FeaturedPpvEvent',
        id: latestStream.id,
        orderIndex: 0,
        stream: {
          __typename: 'Stream',
          id: latestStream.id,
          slug: latestStream.slug || '',
          name: latestStream.name || null,
          imageURL: latestStream.imageURL || null,
          price: latestStream.price || 0,
          description: latestStream.description || '',
          isFree: latestStream.isFree || false,
          streamStatus: latestStream.streamStatus,
          scheduleDateUtc: latestStream.scheduleDateUtc
            ? new Date(latestStream.scheduleDateUtc).toISOString()
            : null,
          type: latestStream.type,
          store: {
            __typename: 'Store',
            id: latestStream.store?.id || '',
            slug: latestStream.store?.slug || null,
          },
          participants: null,
        },
      },
    ];
  }, [athleteStreamsData]);

  // Determine what to display:
  // - If profile owner IS in featured streams → show filtered featured streams (only ones where owner is participant/host)
  // - If profile owner is NOT in featured streams → show athlete's latest stream/interview
  const streamsToDisplay = isProfileOwnerInFeaturedStreams
    ? filteredFeaturedStreams
    : convertedStreams;

  if (!streamsToDisplay?.length) {
    return null;
  }

  return (
    <FeaturedStreamsCarousel
      featuredStreams={streamsToDisplay}
      fullWidth
      useArrowNavigation
      isProfilePage
      noTopBorder
      showOfficialPpvTag={isProfileOwnerInFeaturedStreams}
    />
  );
};

export default ProfileFeaturedStreams;
